import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from "react-bootstrap";
import { FaCloudUploadAlt, FaFileInvoice, FaCheckCircle, FaExclamationTriangle, FaTimesCircle, FaRobot, FaFileCode, FaEye } from "react-icons/fa";
import { procesarArchivoFactura, validarFacturaExtraida } from "../../../services/contabilidad/facturaExtractorService";

const ACCEPTED_TYPES = ".xml,.pdf,.jpg,.jpeg,.png";
const MAX_SIZE_MB = 10;

const formatMonto = (valor) =>
  Number(valor || 0).toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Componente de carga de facturas con extracción inteligente
 * Soporta: XML UBL (SUNAT), PDF, Imágenes (JPG/PNG)
 */
const UploadFacturaExtractor = ({ onFacturaExtraida, onCancelar }) => {
  const [archivo, setArchivo] = useState(null);
  const [procesando, setProcesando] = useState(false);
  const [error, setError] = useState("");
  const [facturaExtraida, setFacturaExtraida] = useState(null);
  const [validacion, setValidacion] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [modoManual, setModoManual] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  
  // Ref para prevenir llamadas duplicadas a la API
  const procesandoRef = useRef(false);

  // Generar URL de vista previa para el archivo subido
  useEffect(() => {
    if (archivo) {
      const url = URL.createObjectURL(archivo);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setPreviewUrl(null);
    }
  }, [archivo]);

  // Validar factura en modo manual cuando cambian los datos
  useEffect(() => {
    if (modoManual && facturaExtraida) {
      const { proveedor, comprobante, montos } = facturaExtraida;
      const errores = [];
      
      if (!proveedor.ruc || proveedor.ruc.length !== 11) errores.push("RUC del proveedor inválido (debe ser 11 dígitos)");
      if (!proveedor.razonSocial?.trim()) errores.push("Razón social del proveedor requerida");
      if (!comprobante.serie?.trim()) errores.push("Serie del comprobante requerida");
      if (!comprobante.numero?.trim()) errores.push("Número del comprobante requerido");
      if (!comprobante.fechaEmision) errores.push("Fecha de emisión requerida");
      if (!montos.subtotal || montos.subtotal <= 0) errores.push("Subtotal debe ser mayor a 0");
      
      setValidacion({
        esValido: errores.length === 0,
        errores,
        advertencias: errores.length === 0 ? [] : ["Complete todos los campos obligatorios"]
      });
    }
  }, [modoManual, facturaExtraida]);

  // Manejar drag & drop
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelect(Array.from(e.dataTransfer.files));
    }
  }, []);

  const getTipoArchivoOriginal = (file) => {
    const ext = file?.name?.split(".").pop().toLowerCase() || "";
    if (ext === "xml") return "xml";
    if (["jpg", "jpeg", "png"].includes(ext)) return "imagen";
    return "pdf";
  };

  const validarArchivo = (file) => {
    if (!file) return "Archivo inválido";

    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      return `El archivo ${file.name} excede el tamaño máximo de ${MAX_SIZE_MB}MB`;
    }

    const ext = file.name.split(".").pop().toLowerCase();
    if (!["xml", "pdf", "jpg", "jpeg", "png"].includes(ext)) {
      return `Tipo de archivo no soportado en ${file.name}. Use XML, PDF o imagen (JPG/PNG)`;
    }

    return "";
  };

  const procesarArchivosMasivo = async (files) => {
    if (!Array.isArray(files) || files.length === 0) return;

    // PROTECCIÓN: Evitar llamadas duplicadas a la API
    if (procesandoRef.current) {
      console.warn("[UploadFactura] ⚠️ Ignorando llamada duplicada - ya hay un proceso en curso");
      return;
    }

    const resultados = [];
    const errores = [];

    try {
      procesandoRef.current = true;
      setProcesando(true);
      setError("");
      setFacturaExtraida(null);
      setValidacion(null);
      setModoManual(false);
      setArchivo(files[0] || null);

      for (const file of files) {
        const errorValidacion = validarArchivo(file);
        if (errorValidacion) {
          errores.push(errorValidacion);
          continue;
        }

        try {
          const factura = await procesarArchivoFactura(file);
          const validacionResult = validarFacturaExtraida(factura);

          if (validacionResult?.esValido) {
            resultados.push({
              ...factura,
              archivoOriginal: file,
              tipoArchivoOriginal: getTipoArchivoOriginal(file)
            });
          } else {
            errores.push(`No se pudo validar ${file.name}. Revise el archivo.`);
          }
        } catch (err) {
          errores.push(`Error procesando ${file.name}: ${err?.message || "Error desconocido"}`);
        }
      }

      if (resultados.length > 0 && onFacturaExtraida) {
        onFacturaExtraida(resultados);
      }

      if (resultados.length === 0) {
        setError(errores[0] || "No se pudo procesar ningún archivo.");
      }
    } finally {
      procesandoRef.current = false;
      setProcesando(false);
    }
  };

  const handleFileSelect = async (filesOrFile) => {
    const files = Array.isArray(filesOrFile) ? filesOrFile : [filesOrFile];
    const filesFiltrados = files.filter(Boolean);
    if (filesFiltrados.length === 0) return;

    setError("");
    setFacturaExtraida(null);
    setValidacion(null);

    if (filesFiltrados.length > 1) {
      await procesarArchivosMasivo(filesFiltrados);
      return;
    }

    const file = filesFiltrados[0];
    const errorValidacion = validarArchivo(file);
    if (errorValidacion) {
      setError(errorValidacion);
      return;
    }

    setArchivo(file);
    
    // Procesar automáticamente
    await procesarArchivo(file);
  };

  const procesarArchivo = async (file) => {
    // PROTECCIÓN: Evitar llamadas duplicadas a la API
    if (procesandoRef.current) {
      console.warn("[UploadFactura] ⚠️ Ignorando llamada duplicada - ya hay un proceso en curso");
      return;
    }
    
    try {
      procesandoRef.current = true; // Marcar que estamos procesando
      setProcesando(true);
      setError("");
      setModoManual(false);

      console.log("[UploadFactura] 📄 Iniciando procesamiento de:", file.name);
      const factura = await procesarArchivoFactura(file);
      console.log("[UploadFactura] ✅ Procesamiento completado");
      
      const validacionResult = validarFacturaExtraida(factura);

      setFacturaExtraida(factura);
      setValidacion(validacionResult);
    } catch (err) {
      console.error("[UploadFactura] ❌ Error:", err);
      
      // Detectar errores de API o servicios OCR
      const esErrorApi = err.message === "RATE_LIMIT" || err.message === "CUOTA_AGOTADA" || err.message === "OCR_NO_DISPONIBLE";
      
      if (err.message === "RATE_LIMIT") {
        setError("Límite de solicitudes por minuto alcanzado. Espere 30 segundos e intente nuevamente, o ingrese los datos manualmente.");
      } else if (err.message === "CUOTA_AGOTADA" || err.message === "OCR_NO_DISPONIBLE") {
        setError("El servicio de lectura OCR de Google Vision no pudo extraer texto del documento. Puede ingresar los datos manualmente o intentar con un archivo XML.");
      } else {
        setError(err.message || "Error procesando el archivo");
      }
      
      // Habilitar modo manual para errores de API
      if (esErrorApi) {
        setModoManual(true);
        // Crear factura vacía para ingreso manual
        setFacturaExtraida({
          proveedor: { ruc: "", razonSocial: "", direccion: "" },
          comprobante: { tipo: "FACTURA", serie: "", numero: "", fechaEmision: "", fechaVencimiento: "", moneda: "PEN" },
          montos: { subtotal: 0, igv: 0, total: 0, descuentos: 0, otrosCargos: 0 },
          items: [],
          cliente: { ruc: "", razonSocial: "" },
          metadatos: { fuente: "MANUAL", confianza: 100, fechaProcesamiento: new Date().toISOString() }
        });
        setValidacion({ esValido: false, errores: ["Complete los datos manualmente"], advertencias: [] });
      }
    } finally {
      procesandoRef.current = false; // Liberar el lock
      setProcesando(false);
    }
  };

  const handleConfirmar = () => {
    if (facturaExtraida && onFacturaExtraida) {
      onFacturaExtraida({
        ...facturaExtraida,
        archivoOriginal: archivo,
        tipoArchivoOriginal: getTipoArchivoOriginal(archivo)
      });
    }
  };

  const handleInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileSelect(Array.from(e.target.files));
      e.target.value = "";
    }
  };

  const limpiar = () => {
    setArchivo(null);
    setFacturaExtraida(null);
    setValidacion(null);
    setError("");
  };

  return (
    <Modal show={true} onHide={onCancelar} size="lg" centered backdrop="static">
      <Modal.Header 
        closeButton 
        className="border-0 pb-0"
        style={{ background: "linear-gradient(135deg, #3a3a3a 0%, #606060 100%)" }}
      >
        <Modal.Title className="text-white d-flex align-items-center gap-2">
          <FaFileInvoice /> Cargar Factura
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-4">
        {error && !modoManual && (
          <Alert variant="danger" dismissible onClose={() => setError("")} className="mb-3">
            <FaTimesCircle className="me-2" />{error}
          </Alert>
        )}

        {/* Alerta de modo manual cuando no se extrae texto OCR */}
        {modoManual && (
          <Alert variant="warning" className="mb-3">
            <div className="d-flex align-items-start gap-2">
              <FaExclamationTriangle className="mt-1 flex-shrink-0" />
              <div className="flex-grow-1">
                <strong>Ingreso de datos en modo manual</strong>
                <p className="mb-2 small">
                  El documento subido es un PDF escaneado de imagen o el servicio Google Cloud Vision no está disponible en este momento. Complete los campos requeridos manualmente o elija otra opción:
                </p>
                <div className="d-flex gap-2 flex-wrap">
                  <Button 
                    size="sm" 
                    variant="outline-primary"
                    onClick={() => { limpiar(); setModoManual(false); }}
                  >
                    📄 Subir XML (100% automático)
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline-secondary"
                    onClick={() => setError("")}
                  >
                    ✏️ Continuar con datos manuales
                  </Button>
                </div>
              </div>
            </div>
          </Alert>
        )}

        {/* Zona de carga */}
        {!facturaExtraida && (
          <div
            className={`border-3 border-dashed rounded-4 p-5 text-center transition-all ${
              dragActive ? "border-primary bg-primary bg-opacity-10" : "border-secondary"
            }`}
            style={{ 
              cursor: "pointer",
              background: dragActive ? "rgba(59, 130, 246, 0.05)" : "#f8f9fa",
              transition: "all 0.3s ease"
            }}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => document.getElementById("facturaInput").click()}
          >
            <input
              id="facturaInput"
              type="file"
              multiple
              accept={ACCEPTED_TYPES}
              onChange={handleInputChange}
              style={{ display: "none" }}
            />

            {procesando ? (
              <div className="py-4">
                <Spinner animation="border" variant="primary" className="mb-3" />
                <h5 className="text-muted">Procesando documento...</h5>
                <p className="text-muted small mb-0">
                  {archivo?.name?.endsWith(".xml") 
                    ? "Parseando XML UBL de SUNAT..." 
                    : "Analizando con inteligencia artificial..."}
                </p>
              </div>
            ) : (
              <>
                <FaCloudUploadAlt size={48} className="text-secondary mb-3" />
                <h5 className="mb-2">Arrastra tu factura aquí</h5>
                <p className="text-muted mb-3">o haz clic para seleccionar</p>
                <div className="d-flex justify-content-center gap-2 flex-wrap">
                  <Badge bg="light" text="dark" className="px-3 py-2">
                    <FaFileCode className="me-1" /> XML (SUNAT)
                  </Badge>
                  <Badge bg="light" text="dark" className="px-3 py-2">
                    📄 PDF
                  </Badge>
                  <Badge bg="light" text="dark" className="px-3 py-2">
                    🖼️ Imagen
                  </Badge>
                </div>
                <p className="text-muted small mt-3 mb-0">
                  Máximo {MAX_SIZE_MB}MB · XML tiene 100% precisión · PDF/Imagen usa Google Vision OCR
                </p>
              </>
            )}
          </div>
        )}

        {/* Resultado de extracción */}
        {facturaExtraida && (
          <div className="animate__animated animate__fadeIn">
            {/* Badge de fuente */}
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="d-flex align-items-center gap-2">
                {modoManual || facturaExtraida.metadatos?.fuente === "MANUAL" ? (
                  <Badge bg="warning" text="dark" className="px-3 py-2">
                    ✍️ Ingreso Manual
                  </Badge>
                ) : facturaExtraida.metadatos?.fuente === "QWEN_VISION" ? (
                  <Badge bg="dark" className="px-3 py-2 text-white" style={{ backgroundColor: "#6f42c1" }}>
                    <FaRobot className="me-1" /> Qwen Vision AI · {facturaExtraida.metadatos.confianza}% Confianza
                  </Badge>
                ) : facturaExtraida.metadatos?.fuente === "XML_UBL" ? (
                  <Badge bg="success" className="px-3 py-2">
                    <FaCheckCircle className="me-1" /> XML SUNAT · 100% Precisión
                  </Badge>
                ) : facturaExtraida.metadatos?.fuente === "PDF_TEXT" ? (
                  <Badge bg="primary" className="px-3 py-2">
                    <FaRobot className="me-1" /> PDF Texto · {facturaExtraida.metadatos.confianza}% Confianza
                  </Badge>
                ) : (
                  <Badge bg="info" className="px-3 py-2">
                    <FaRobot className="me-1" /> Google Cloud Vision · {facturaExtraida.metadatos.confianza}% Confianza
                  </Badge>
                )}
                {archivo?.type === "application/pdf" && previewUrl && (
                  <Button 
                    variant="outline-primary" 
                    size="sm" 
                    className="d-flex align-items-center gap-1 rounded-pill px-3"
                    onClick={() => window.open(previewUrl, "_blank")}
                  >
                    <FaEye /> Ver PDF
                  </Button>
                )}
                <Badge bg="secondary" className="px-2 py-1">
                  {archivo?.name}
                </Badge>
              </div>
              <Button variant="link" size="sm" onClick={limpiar}>
                Cargar otro
              </Button>
            </div>

            {/* Alertas de validación (solo en extracción automática) */}
            {!modoManual && validacion?.errores?.length > 0 && (
              <Alert variant="danger" className="mb-3">
                <strong><FaTimesCircle className="me-2" />Errores encontrados:</strong>
                <ul className="mb-0 mt-2">
                  {validacion.errores.map((err, i) => <li key={i}>{err}</li>)}
                </ul>
              </Alert>
            )}

            {!modoManual && validacion?.advertencias?.length > 0 && (
              <Alert variant="warning" className="mb-3">
                <strong><FaExclamationTriangle className="me-2" />Advertencias:</strong>
                <ul className="mb-0 mt-2">
                  {validacion.advertencias.map((adv, i) => <li key={i}>{adv}</li>)}
                </ul>
              </Alert>
            )}

            {/* Datos del proveedor */}
            <Card className="border-0 shadow-sm mb-3">
              <Card.Header className="bg-light border-0 py-2">
                <span className="fw-semibold text-secondary small text-uppercase">
                  Proveedor {modoManual && <Badge bg="warning" text="dark" className="ms-2">Ingreso manual</Badge>}
                </span>
              </Card.Header>
              <Card.Body className="py-3">
                <Row>
                  <Col md={4}>
                    <Form.Label className="small text-muted mb-1">RUC</Form.Label>
                    <Form.Control
                      size="sm"
                      value={facturaExtraida.proveedor.ruc || ""}
                      readOnly={!modoManual}
                      onChange={(e) => modoManual && setFacturaExtraida(f => ({
                        ...f,
                        proveedor: { ...f.proveedor, ruc: e.target.value }
                      }))}
                      className={!facturaExtraida.proveedor.ruc ? "border-danger" : ""}
                      placeholder={modoManual ? "20XXXXXXXXX" : ""}
                    />
                  </Col>
                  <Col md={8}>
                    <Form.Label className="small text-muted mb-1">Razón Social</Form.Label>
                    <Form.Control
                      size="sm"
                      value={facturaExtraida.proveedor.razonSocial || ""}
                      readOnly={!modoManual}
                      onChange={(e) => modoManual && setFacturaExtraida(f => ({
                        ...f,
                        proveedor: { ...f.proveedor, razonSocial: e.target.value }
                      }))}
                      placeholder={modoManual ? "Nombre del proveedor" : ""}
                    />
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Datos del comprobante */}
            <Card className="border-0 shadow-sm mb-3">
              <Card.Header className="bg-light border-0 py-2">
                <span className="fw-semibold text-secondary small text-uppercase">Comprobante</span>
              </Card.Header>
              <Card.Body className="py-3">
                <Row className="g-2">
                  <Col md={3}>
                    <Form.Label className="small text-muted mb-1">Tipo</Form.Label>
                    {modoManual ? (
                      <Form.Select 
                        size="sm" 
                        value={facturaExtraida.comprobante.tipo || "FACTURA"}
                        onChange={(e) => setFacturaExtraida(f => ({
                          ...f,
                          comprobante: { ...f.comprobante, tipo: e.target.value }
                        }))}
                      >
                        <option value="FACTURA">Factura</option>
                        <option value="BOLETA">Boleta</option>
                        <option value="NOTA_CREDITO">Nota Crédito</option>
                        <option value="NOTA_DEBITO">Nota Débito</option>
                      </Form.Select>
                    ) : (
                      <Form.Control size="sm" value={facturaExtraida.comprobante.tipo || ""} readOnly />
                    )}
                  </Col>
                  <Col md={2}>
                    <Form.Label className="small text-muted mb-1">Serie</Form.Label>
                    <Form.Control 
                      size="sm" 
                      value={facturaExtraida.comprobante.serie || ""} 
                      readOnly={!modoManual}
                      onChange={(e) => modoManual && setFacturaExtraida(f => ({
                        ...f,
                        comprobante: { ...f.comprobante, serie: e.target.value.toUpperCase() }
                      }))}
                      className={!facturaExtraida.comprobante.serie ? "border-danger" : ""}
                      placeholder={modoManual ? "F001" : ""}
                    />
                  </Col>
                  <Col md={2}>
                    <Form.Label className="small text-muted mb-1">Número</Form.Label>
                    <Form.Control 
                      size="sm" 
                      value={facturaExtraida.comprobante.numero || ""} 
                      readOnly={!modoManual}
                      onChange={(e) => modoManual && setFacturaExtraida(f => ({
                        ...f,
                        comprobante: { ...f.comprobante, numero: e.target.value }
                      }))}
                      className={!facturaExtraida.comprobante.numero ? "border-danger" : ""}
                      placeholder={modoManual ? "00001234" : ""}
                    />
                  </Col>
                  <Col md={3}>
                    <Form.Label className="small text-muted mb-1">Fecha Emisión</Form.Label>
                    <Form.Control 
                      size="sm" 
                      type={modoManual ? "date" : "text"}
                      value={facturaExtraida.comprobante.fechaEmision || ""} 
                      readOnly={!modoManual}
                      onChange={(e) => modoManual && setFacturaExtraida(f => ({
                        ...f,
                        comprobante: { ...f.comprobante, fechaEmision: e.target.value }
                      }))}
                      className={!facturaExtraida.comprobante.fechaEmision ? "border-danger" : ""}
                    />
                  </Col>
                  <Col md={2}>
                    <Form.Label className="small text-muted mb-1">Moneda</Form.Label>
                    {modoManual ? (
                      <Form.Select 
                        size="sm" 
                        value={facturaExtraida.comprobante.moneda || "PEN"}
                        onChange={(e) => setFacturaExtraida(f => ({
                          ...f,
                          comprobante: { ...f.comprobante, moneda: e.target.value }
                        }))}
                      >
                        <option value="PEN">PEN</option>
                        <option value="USD">USD</option>
                      </Form.Select>
                    ) : (
                      <Form.Control size="sm" value={facturaExtraida.comprobante.moneda || "PEN"} readOnly />
                    )}
                  </Col>
                </Row>
                <Row className="g-2 mt-1">
                  <Col md={3}>
                    <Form.Label className="small text-muted mb-1">Fecha Vencimiento</Form.Label>
                    <Form.Control 
                      size="sm" 
                      type={modoManual ? "date" : "text"}
                      value={facturaExtraida.comprobante.fechaVencimiento || ""} 
                      readOnly={!modoManual}
                      onChange={(e) => modoManual && setFacturaExtraida(f => ({
                        ...f,
                        comprobante: { ...f.comprobante, fechaVencimiento: e.target.value }
                      }))}
                    />
                  </Col>
                </Row>
                {(facturaExtraida.comprobante.tipo === "NOTA_CREDITO" || facturaExtraida.comprobante.tipo === "07" || String(facturaExtraida.comprobante.serie || "").toUpperCase().startsWith("FC") || String(facturaExtraida.comprobante.serie || "").toUpperCase().startsWith("EC")) && (
                  <Row className="mt-3">
                    <Col md={12}>
                      <div className="p-2 bg-light border rounded-3" style={{ borderLeft: "4px solid #7c3aed" }}>
                        <Form.Label className="small fw-bold text-primary mb-1">
                          🧾 Factura / Comprobante Afectado que Anula esta Nota de Crédito
                        </Form.Label>
                        <Form.Control
                          size="sm"
                          type="text"
                          placeholder="Ej. E001-114 o F001-14"
                          value={facturaExtraida.comprobante.documentoModificado || ""}
                          onChange={(e) => setFacturaExtraida(f => ({
                            ...f,
                            comprobante: { ...f.comprobante, documentoModificado: e.target.value.toUpperCase() }
                          }))}
                        />
                        <small className="extra-small text-muted d-block mt-1">
                          Ingrese o verifique el correlativo de la factura original para cancelar su deuda y desbloquear la cuenta del cliente.
                        </small>
                      </div>
                    </Col>
                  </Row>
                )}
              </Card.Body>
            </Card>

            {/* Montos */}
            <Card className="border-0 shadow-sm mb-3">
              <Card.Header className="bg-light border-0 py-2">
                <span className="fw-semibold text-secondary small text-uppercase">Montos</span>
              </Card.Header>
              <Card.Body className="py-3">
                <Row className="g-2">
                  <Col md={3}>
                    <Form.Label className="small text-muted mb-1">Subtotal</Form.Label>
                    <Form.Control 
                      size="sm" 
                      type={modoManual ? "number" : "text"}
                      step="0.01"
                      value={modoManual ? (facturaExtraida.montos.subtotal || "") : formatMonto(facturaExtraida.montos.subtotal)} 
                      readOnly={!modoManual}
                      onChange={(e) => {
                        if (modoManual) {
                          const subtotal = parseFloat(e.target.value) || 0;
                          const igv = Math.round(subtotal * 0.18 * 100) / 100;
                          const total = Math.round((subtotal + igv) * 100) / 100;
                          setFacturaExtraida(f => ({
                            ...f,
                            montos: { ...f.montos, subtotal, igv, total }
                          }));
                        }
                      }}
                      className={`text-end ${!facturaExtraida.montos.subtotal && modoManual ? "border-danger" : ""}`}
                      placeholder={modoManual ? "0.00" : ""}
                    />
                  </Col>
                  <Col md={3}>
                    <Form.Label className="small text-muted mb-1">IGV (18%)</Form.Label>
                    <Form.Control 
                      size="sm" 
                      value={formatMonto(facturaExtraida.montos.igv)} 
                      readOnly 
                      className="text-end bg-light"
                    />
                    {modoManual && <Form.Text className="text-muted" style={{fontSize: "0.7rem"}}>Auto-calculado</Form.Text>}
                  </Col>
                  <Col md={3}>
                    <Form.Label className="small text-muted mb-1">Otros</Form.Label>
                    <Form.Control 
                      size="sm" 
                      type={modoManual ? "number" : "text"}
                      step="0.01"
                      value={modoManual ? (facturaExtraida.montos.otrosCargos || 0) : formatMonto(facturaExtraida.montos.otrosCargos || 0)} 
                      readOnly={!modoManual}
                      onChange={(e) => {
                        if (modoManual) {
                          const otrosCargos = parseFloat(e.target.value) || 0;
                          const subtotal = facturaExtraida.montos.subtotal || 0;
                          const igv = facturaExtraida.montos.igv || 0;
                          const total = Math.round((subtotal + igv + otrosCargos) * 100) / 100;
                          setFacturaExtraida(f => ({
                            ...f,
                            montos: { ...f.montos, otrosCargos, total }
                          }));
                        }
                      }}
                      className="text-end"
                    />
                  </Col>
                  <Col md={3}>
                    <Form.Label className="small text-muted mb-1 fw-bold">TOTAL</Form.Label>
                    <Form.Control 
                      size="sm" 
                      value={formatMonto(facturaExtraida.montos.total)} 
                      readOnly 
                      className="text-end fw-bold bg-success bg-opacity-10"
                    />
                    {modoManual && <Form.Text className="text-muted" style={{fontSize: "0.7rem"}}>Auto-calculado</Form.Text>}
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Items (si hay) */}
            {facturaExtraida.items?.length > 0 && (
              <Card className="border-0 shadow-sm mb-3">
                <Card.Header className="bg-light border-0 py-2">
                  <span className="fw-semibold text-secondary small text-uppercase">
                    Detalle ({facturaExtraida.items.length} items)
                  </span>
                </Card.Header>
                <Card.Body className="p-0">
                  <div style={{ maxHeight: 150, overflowY: "auto" }}>
                    <Table size="sm" className="mb-0" style={{ fontSize: "0.8rem" }}>
                      <thead className="bg-light">
                        <tr>
                          <th className="py-2 px-2">Descripción</th>
                          <th className="py-2 px-2 text-center">Cant.</th>
                          <th className="py-2 px-2 text-end">P.Unit</th>
                          <th className="py-2 px-2 text-end">Subtotal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {facturaExtraida.items.map((item, idx) => (
                          <tr key={idx}>
                            <td className="py-1 px-2 text-truncate" style={{ maxWidth: 250 }}>
                              {item.descripcion || "-"}
                            </td>
                            <td className="py-1 px-2 text-center">{item.cantidad}</td>
                            <td className="py-1 px-2 text-end">{formatMonto(item.precioUnitario)}</td>
                            <td className="py-1 px-2 text-end">{formatMonto(item.subtotal)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            )}

            {/* Mostrar errores de validación en modo manual */}
            {modoManual && validacion && !validacion.esValido && validacion.errores.length > 0 && (
              <Alert variant="info" className="mb-0 py-2">
                <small>
                  <strong>Campos pendientes:</strong>
                  <ul className="mb-0 ps-3 mt-1">
                    {validacion.errores.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                </small>
              </Alert>
            )}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="border-0 pt-0">
        <Button variant="outline-secondary" onClick={onCancelar}>
          Cancelar
        </Button>
        {facturaExtraida && (
          <Button 
            variant="success" 
            onClick={handleConfirmar}
            disabled={!validacion?.esValido}
            className="px-4"
          >
            <FaCheckCircle className="me-2" />
            {modoManual ? "Confirmar datos manuales" : "Usar estos datos"}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default UploadFacturaExtractor;
